import {
  goRequest as request,
  goClient,
  formGoClient as formClient,
} from '../utils/httpClientTs';
import {
  MarketplaceRequestParams,
  MarketplaceItemRequest,
  MarketplaceItemUpdateRequest,
  MarketplaceItemResponse,
  MarketplaceResponse,
  MarketplaceCategory,
  MarketplaceSubcategory,
} from '../types/marketplace';

// Marketplace Items CRUD operations
export const getMarketplaceItems = (
  params?: MarketplaceRequestParams
): Promise<MarketplaceResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.page_size)
    queryParams.append('per_page', params.page_size.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.category_id)
    queryParams.append('category_id', params.category_id);
  if (params?.subcategory_id)
    queryParams.append('subcategory_id', params.subcategory_id);
  if (params?.local_chapter_id)
    queryParams.append('local_chapter_id', params.local_chapter_id);
  if (params?.spd_chapter_id)
    queryParams.append('spd_chapter_id', params.spd_chapter_id);
  if (params?.status_id) queryParams.append('status_id', params.status_id);
  if (params?.my_items) queryParams.append('my_items', 'true');
  if (params?.order_by) queryParams.append('order_by', params.order_by);
  if (params?.order) queryParams.append('order', params.order);

  return request({
    url: `/shops/marketplace/items?${queryParams.toString()}`,
    method: 'GET',
  });
};

export const getMarketplaceItem = (
  id: string
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}`,
    method: 'GET',
  });
};

export const createMarketplaceItem = (
  data: MarketplaceItemRequest
): Promise<MarketplaceItemResponse> => {
  return request({
    url: '/shops/marketplace/items',
    method: 'POST',
    data,
  });
};

export const updateMarketplaceItem = (
  id: string,
  data: MarketplaceItemUpdateRequest
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}`,
    method: 'PUT',
    data,
  });
};

// Status change functions (replacing delete functionality)
export const markItemAsSold = (
  id: string
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}/status`,
    method: 'PUT',
    data: { status_id: '4' }, // Sold status
  });
};

export const markItemAsInactive = (
  id: string
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}/status`,
    method: 'PUT',
    data: { status_id: '5' }, // Inactive status
  });
};

// Approval workflow
export const approveMarketplaceItem = (
  id: string
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}/approve`,
    method: 'POST',
  });
};

export const rejectMarketplaceItem = (
  id: string,
  reason?: string
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}/reject`,
    method: 'POST',
    data: { reason },
  });
};

export const removeMarketplaceItem = (
  id: string
): Promise<MarketplaceItemResponse> => {
  return request({
    url: `/shops/marketplace/items/${id}/removed`,
    method: 'POST',
  });
};

// Categories and subcategories
export const getMarketplaceCategories = (): Promise<MarketplaceCategory[]> => {
  return request({
    url: '/shops/marketplace/categories',
    method: 'GET',
  });
};

export const getMarketplaceSubcategories = (
  categoryId?: string
): Promise<MarketplaceSubcategory[]> => {
  const url = categoryId
    ? `/shops/marketplace/subcategories?category_id=${categoryId}`
    : '/shops/marketplace/subcategories';
  return request({
    url,
    method: 'GET',
  });
};

// Statuses
export const getMarketplaceStatuses = (): Promise<
  { id: string; name: string }[]
> => {
  return request({
    url: '/shops/marketplace/statuses',
    method: 'GET',
  });
};

// Payment processing for ad creation fee
export const createMarketplacePayment = (data: {
  item_id: string;
  amount: number;
  payment_method_id: string;
}) => {
  // Mock implementation - in real app this would integrate with Stripe
  return Promise.resolve({
    id: `payment_${Date.now()}`,
    status: 'succeeded',
    amount: data.amount,
    item_id: data.item_id,
  });
};

// Image management
export const uploadMarketplaceImage = (
  file: File
): Promise<{ data: { id: string } }> => {
  const formData = new FormData();
  formData.append('image', file);

  return formClient.post('/shops/marketplace/items/images', formData);
};

export const uploadMarketplaceImageToItem = (
  file: File,
  itemId: string
): Promise<{ data: { id: string } }> => {
  const formData = new FormData();
  formData.append('image', file);

  return formClient.post(`/shops/marketplace/items/${itemId}/images`, formData);
};

export const deleteMarketplaceImage = (
  itemId: string,
  imageId: string
): Promise<{ message: string }> => {
  return request({
    url: `/shops/marketplace/items/${itemId}/images/${imageId}`,
    method: 'DELETE',
  });
};

// Document management
export const uploadMarketplaceDocument = (
  file: File
): Promise<{ data: { id: string } }> => {
  const formData = new FormData();
  formData.append('document', file);

  return formClient.post('/shops/marketplace/items/documents', formData);
};

export const uploadMarketplaceDocumentToItem = (
  file: File,
  itemId: string
): Promise<{ data: { id: string } }> => {
  const formData = new FormData();
  formData.append('document', file);

  return formClient.post(
    `/shops/marketplace/items/${itemId}/documents`,
    formData
  );
};

export const deleteMarketplaceDocument = (
  itemId: string,
  documentId: string
): Promise<{ id: string }> => {
  return request({
    url: `/shops/marketplace/items/${itemId}/documents/${documentId}`,
    method: 'DELETE',
  });
};

// Download marketplace document via backend (auth required). Pass the relative download URL from item.document.url.
export const downloadMarketplaceDocumentBlob = async (
  downloadPath: string
): Promise<{ blob: Blob; filename?: string }> => {
  const response = await goClient.get(downloadPath, {
    responseType: 'blob',
  });
  const blob = response.data as Blob;
  const contentDisposition = response.headers?.['content-disposition'];
  let filename: string | undefined;
  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (match?.[1]) {
      filename = match[1].replace(/['"]/g, '').trim();
    }
  }
  return { blob, filename };
};
